import React, { useState, useEffect } from 'react';
import { 
  // `ArchiveRestore` et non `RotateCcw` sur le bouton « Restaurer » : voir le
  // commentaire d'import de pages/ElevesRadies.jsx. Décision du 05/10/2026.
  Archive, ArchiveRestore, ArrowLeft, Search, Users, Calendar,
  AlertCircle, Loader2, ChevronDown, ChevronRight,
  UserMinus, Network, X, HelpCircle, Info, Check,
  BookOpen, Shield
} from 'lucide-react';
import { Button } from './ui/button';
import { api } from '../services/api';
import { Toast } from '../components/ui/Toast';
import { LongToast, shouldUseLongToast } from './ui/LongToast';
import ConfirmDialog from './ConfirmDialog';
import LoadingSpinner from './LoadingSpinner';
import { useFeatures } from '../contexts/FeaturesContext';

const ITEMS_PER_PAGE = 10;

const ArchiveFamilles = ({ anneeScolaireId, onClose }) => {
  // Le guide d'archivage explique des cas inter-sites — desactivation par le
  // site de reference, re-liaison apres restauration. Dans une ecole seule,
  // ils decrivent des gestes introuvables.
  const { estActive } = useFeatures();
  const interSitesOuvert = estActive('module.intersites');
  const [loading, setLoading] = useState(true);
  const [archives, setArchives] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showRestored, setShowRestored] = useState(false);
  const [showGuide, setShowGuide] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [toast, setToast] = useState({ visible: false, message: '', type: 'success' });

  const [showConfirmRestaurer, setShowConfirmRestaurer] = useState(false);
  const [archiveARestaurer, setArchiveARestaurer] = useState(null);
  const [restaurationLoading, setRestaurationLoading] = useState(false);

  useEffect(() => { loadArchives(); }, [showRestored]);

  const loadArchives = async () => {
    setLoading(true);
    try {
      const result = await api.familles.getArchives(showRestored);
      if (result.success) setArchives(result.data || []);
    } catch (error) {
      console.error('Erreur chargement archives:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRestaurer = async () => {
    if (!archiveARestaurer) return;
    setRestaurationLoading(true);
    try {
      const result = await api.familles.restaurerFamille(archiveARestaurer.id, {
        annee_scolaire_id: anneeScolaireId, note: ''
      });
      if (result.success) {
        setToast({ visible: true, message: `Famille "${result.famille_nom}" restaurée avec ${result.nb_eleves_restaures} élève(s) !`, type: 'success' });
        setShowConfirmRestaurer(false);
        setArchiveARestaurer(null);
        await loadArchives();
      } else {
        // Refus : bandeau persistant. Le succès voisin, lui, garde ses 3 secondes
        // — la famille réapparaît dans la liste, l'écran le dit déjà.
        setToast({ visible: true, message: result.message || 'Erreur restauration', type: 'error', persistent: true });
      }
    } catch (error) {
      setToast({ visible: true, message: 'Erreur lors de la restauration', type: 'error', persistent: true });
    } finally {
      setRestaurationLoading(false);
    }
  };

  const fmt = (m) => new Intl.NumberFormat('fr-FR').format(Math.round(m || 0)) + ' FCFA';
  const fmtDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  // Filtrage + pagination
  const filtered = archives.filter(a => !searchTerm || a.famille_nom.toLowerCase().includes(searchTerm.toLowerCase()));
  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginatedArchives = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const getTypeBadge = (type) => {
    switch (type) {
      case 'manuel': return { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Manuel', icon: Archive };
      case 'auto_intersite': return { bg: 'bg-teal-100', text: 'text-teal-700', label: 'Inter-Site', icon: Network };
      case 'auto_radiation': return { bg: 'bg-red-100', text: 'text-red-700', label: 'Radiation', icon: UserMinus };
      default: return { bg: 'bg-gray-100', text: 'text-gray-700', label: type, icon: Archive };
    }
  };

  return (
    <div className="w-full min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm mb-6">
        <Button variant="ghost" className="mb-3 text-[#246990] hover:bg-[#246990]/10 w-fit flex items-center" onClick={onClose}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Retour aux familles
        </Button>

        <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 flex items-center mb-5">
          <div className="p-2 bg-amber-500 rounded-full mr-4">
            <Archive className="text-white" size={20} />
          </div>
          <div className="flex-1">
            <h1 className="font-medium text-amber-800 text-xl">Familles Archivées</h1>
            <p className="text-sm text-amber-600">Historique des familles désactivées - Restauration possible</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-2xl font-bold text-amber-700">{archives.filter(a => !a.is_restored).length}</div>
              <div className="text-sm text-amber-500">en archive</div>
            </div>
            <button
              onClick={() => setShowGuide(!showGuide)}
              className={`p-2 rounded-lg transition-colors ${showGuide ? 'bg-amber-200 text-amber-800' : 'bg-amber-100 text-amber-600 hover:bg-amber-200'}`}
              title="Afficher/masquer le guide"
            >
              <HelpCircle size={20} />
            </button>
          </div>
        </div>

        {/* ══════════════════════════════════════════════ */}
        {/* GUIDE EXPLICATIF COMPLET                      */}
        {/* ══════════════════════════════════════════════ */}
        {showGuide && (
          <div className="mb-5 p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-blue-800 flex items-center gap-2">
                <BookOpen size={16} />
                Guide : Comment fonctionnent les archives ?
              </h3>
              <button onClick={() => setShowGuide(false)} className="text-blue-400 hover:text-blue-600">
                <X size={16} />
              </button>
            </div>

            {/* Types d'archivage */}
            <div>
              <p className="text-xs font-semibold text-blue-700 mb-2">Pourquoi une famille se retrouve-t-elle ici ?</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="p-3 bg-white rounded-lg border border-blue-100">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] font-bold">MANUEL</span>
                  </div>
                  <p className="text-[11px] text-gray-600">
                    Un administrateur a archivé cette famille volontairement (départ de la famille, fin de scolarité, erreur, etc.). 
                    Selon le choix fait lors de l'archivage, les élèves ont été <strong>radiés</strong> ou sont restés <strong>actifs en individuel</strong>.
                  </p>
                </div>
                {interSitesOuvert && (
                <div className="p-3 bg-white rounded-lg border border-teal-100">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="px-2 py-0.5 bg-teal-100 text-teal-700 rounded text-[10px] font-bold">INTER-SITE</span>
                  </div>
                  <p className="text-[11px] text-gray-600">
                    La famille a été archivée automatiquement suite à une <strong>désactivation du mode inter-site</strong> par le site de référence. 
                    Cela arrive quand ce site avait moins de 2 membres dans la famille. Les élèves ne sont <strong>pas radiés</strong>, ils deviennent individuels.
                  </p>
                </div>
                )}
                <div className="p-3 bg-white rounded-lg border border-red-100">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-[10px] font-bold">RADIATION</span>
                  </div>
                  <p className="text-[11px] text-gray-600">
                    Tous les élèves de la famille ont été radiés individuellement, la famille est devenue vide et a été archivée automatiquement.
                  </p>
                </div>
              </div>
            </div>

            {/* Restauration */}
            <div>
              <p className="text-xs font-semibold text-blue-700 mb-2">Que se passe-t-il quand je restaure ?</p>
              <div className="p-3 bg-white rounded-lg border border-blue-100 space-y-2">
                <div className="flex items-start gap-2">
                  <ArchiveRestore size={12} className="text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-[11px] text-gray-600">
                    La famille redevient <strong>active</strong> avec ses réductions d'origine. Les élèves qui étaient dans la famille au moment de l'archivage 
                    sont <strong>réintégrés automatiquement</strong>, sauf s'ils ont été rattachés à une autre famille entre-temps. 
                    Si radiés, ils redeviennent actifs. Les paiements non réglés sont <strong>recalculés</strong> avec les réductions famille.
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <AlertCircle size={12} className="text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-[11px] text-gray-600">
                    <strong>Important :</strong> Les paiements déjà effectués ne sont <strong>jamais modifiés</strong>. Seuls les mois non payés sont recalculés.
                  </p>
                </div>
              </div>
            </div>

            {interSitesOuvert && (<>
            {/* Cas inter-site */}
            <div>
              <p className="text-xs font-semibold text-blue-700 mb-2">Cas particulier : famille inter-site</p>
              <div className="p-3 bg-white rounded-lg border border-teal-100 space-y-2">
                <div className="flex items-start gap-2">
                  <Network size={12} className="text-teal-600 flex-shrink-0 mt-0.5" />
                  <p className="text-[11px] text-gray-600">
                    Si la famille était inter-site avant l'archivage, la restauration <strong>ne réactive pas</strong> automatiquement le mode inter-site. 
                    La famille restaurée sera une <strong>famille locale indépendante</strong>.
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <Info size={12} className="text-blue-600 flex-shrink-0 mt-0.5" />
                  <p className="text-[11px] text-gray-600">
                    Pour re-lier la famille au réseau inter-sites après restauration : ouvrez la famille restaurée, puis utilisez le menu 
                    <strong> Actions → Activer Inter-Site</strong> pour la rendre à nouveau visible aux autres sites du groupe.
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <Shield size={12} className="text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-[11px] text-gray-600">
                    <strong>Attention aux doublons :</strong> Si la famille existe déjà sur un autre site (restaurée indépendamment là-bas), 
                    vérifiez qu'il n'y a pas de famille en double avant de réactiver l'inter-site. Dans ce cas, activez l'inter-site depuis 
                    un seul site, puis rattachez les élèves de l'autre site via le bouton "Lier à famille inter-site".
                  </p>
                </div>
              </div>
            </div>
            </>)}

            {/* Famille avec 1 seul élève */}
            <div>
              <p className="text-xs font-semibold text-blue-700 mb-2">Famille restaurée avec un seul élève</p>
              <div className="p-3 bg-white rounded-lg border border-amber-100">
                <div className="flex items-start gap-2">
                  <AlertCircle size={12} className="text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-[11px] text-gray-600">
                    Une famille nombreuse nécessite au minimum 2 élèves. Si la famille restaurée ne contient qu'un seul élève, 
                    vous disposez d'un <strong>délai de 7 jours</strong> pour ajouter un deuxième élève via le bouton 
                    <strong> "Ajouter un élève"</strong> dans la vue détail de la famille. 
                    Passé ce délai, la famille sera <strong>automatiquement re-archivée</strong>.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Barre de recherche + toggle */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input 
              type="text" 
              placeholder="Rechercher une famille archivée..." 
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-transparent" 
            />
          </div>
          
          {/* Toggle stylisé */}
          <button
            onClick={() => { setShowRestored(!showRestored); setCurrentPage(1); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                showRestored
                ? 'bg-[#319190]/10 text-[#319190] border-2 border-[#319190]/40'
                : 'bg-white text-gray-500 border-2 border-gray-200 hover:border-gray-300'
            }`}
            >
            <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all ${
                showRestored ? 'border-[#319190] bg-[#319190]' : 'border-gray-300 bg-white'
            }`}>
                {showRestored && <Check className="w-3 h-3 text-white" />}
            </div>
            Inclure les restaurées
            </button>
        </div>
      </div>

      {/* Liste des archives */}
      {loading ? (
        <div className="flex justify-center items-center h-64"><LoadingSpinner message="Chargement des archives…" size="lg" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-12 text-center">
          <Archive className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune famille archivée</h3>
          <p className="text-sm text-gray-500">{searchTerm ? 'Aucune archive ne correspond' : 'Les familles archivées apparaîtront ici'}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {paginatedArchives.map((archive) => {
            const isExpanded = expandedId === archive.id;
            const typeBadge = getTypeBadge(archive.type_archivage);
            const TypeIcon = typeBadge.icon;
            const eleves = archive.eleves_snapshot || [];

            return (
              <div key={archive.id} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                {/* Ligne principale */}
                <div
                  onClick={() => setExpandedId(isExpanded ? null : archive.id)}
                  className={`p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors ${archive.is_restored ? 'opacity-50' : ''}`}
                >
                  <div className="flex items-center gap-4">
                    <button className="text-amber-600">
                      {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                    </button>
                    <div className="w-12 h-12 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center">
                      <TypeIcon size={20} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900 text-base">{archive.famille_nom}</span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${typeBadge.bg} ${typeBadge.text}`}>{typeBadge.label}</span>
                        {interSitesOuvert && archive.was_intersite === 1 && (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-teal-50 text-teal-600 border border-teal-200">
                            Ex Inter-Site
                          </span>
                        )}
                        {archive.is_restored === 1 && (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-100 text-green-700">✓ Restaurée</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                        <span className="flex items-center gap-1"><Calendar size={10} />{fmtDate(archive.archived_at)}</span>
                        <span>•</span>
                        <span>{archive.nombre_enfants} élève{archive.nombre_enfants > 1 ? 's' : ''}</span>
                        {archive.archived_by && (<><span>•</span><span>Par {archive.archived_by}</span></>)}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="text-sm font-bold text-gray-900">{fmt(archive.total_paye)}</div>
                      <div className="text-xs text-gray-500">payé / {fmt(archive.total_du)}</div>
                    </div>
                    {!archive.is_restored && (
                      <Button
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setArchiveARestaurer(archive);
                          setShowConfirmRestaurer(true);
                        }}
                        className="bg-[#319190] hover:bg-[#2b7f7e] text-white font-semibold flex items-center gap-1.5"
                      >
                        <ArchiveRestore size={14} /> Restaurer
                      </Button>
                    )}
                  </div>
                </div>

                {/* Détail expansé */}
                {isExpanded && (
                  <div className="px-6 pb-5 bg-gray-50 border-t border-gray-100">
                    <div className="pt-4 space-y-4">
                      {/* Motif */}
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="text-xs font-semibold text-amber-800 mb-1">Motif d'archivage</div>
                        <div className="text-sm text-amber-700">{archive.motif_archivage}</div>
                        {archive.motif_detail && <div className="text-xs text-amber-600 mt-1 italic">{archive.motif_detail}</div>}
                      </div>

                      {/* Tooltip contextuel selon le type */}
                      {archive.type_archivage === 'auto_intersite' && (
                        <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg flex items-start gap-2">
                          <Network size={14} className="text-teal-600 flex-shrink-0 mt-0.5" />
                          <div className="text-xs text-teal-700">
                            <p className="font-semibold mb-1">Archivée par désactivation inter-site</p>
                            <p>
                              Le site de référence a désactivé le mode inter-site. Cette famille avait moins de 2 membres sur ce site 
                              et a été automatiquement archivée. Les élèves sont restés actifs en tant qu'élèves individuels.
                            </p>
                            <p className="mt-1 font-medium">
                              Pour restaurer : cliquez "Restaurer", puis ajoutez un 2ème élève si nécessaire. 
                              Si vous souhaitez re-lier au réseau inter-site, activez l'inter-site depuis la famille restaurée.
                            </p>
                          </div>
                        </div>
                      )}

                      {archive.type_archivage === 'manuel' && (
                        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
                          <Info size={14} className="text-blue-600 flex-shrink-0 mt-0.5" />
                          <div className="text-xs text-blue-700">
                            <p className="font-semibold mb-1">Archivage manuel</p>
                            <p>
                              Cette famille a été archivée volontairement par un administrateur. 
                              {eleves.some(e => e.statut_avant === 'actif') 
                                ? ' Les élèves ont pu être radiés lors de l\'archivage. La restauration les réintégrera automatiquement dans la famille, sauf s\'ils ont été rattachés à une autre famille entre-temps.'
                                : ' Les élèves étaient déjà inactifs au moment de l\'archivage.'}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Stats financières */}
                      <div className="grid grid-cols-3 gap-3">
                        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-center">
                          <div className="text-xs text-blue-600 font-medium">Total dû</div>
                          <div className="text-lg font-bold text-blue-800">{fmt(archive.total_du)}</div>
                        </div>
                        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-center">
                          <div className="text-xs text-green-600 font-medium">Total payé</div>
                          <div className="text-lg font-bold text-green-800">{fmt(archive.total_paye)}</div>
                        </div>
                        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-center">
                          <div className="text-xs text-red-600 font-medium">Reste</div>
                          <div className="text-lg font-bold text-red-800">{fmt(archive.reste_a_payer)}</div>
                        </div>
                      </div>

                      {/* Réductions portées par la famille.
                          Ces deux montants se lisaient comme des réductions
                          accordées aux élèves ci-dessous. Ce sont les valeurs de
                          référence de la FAMILLE : la mensuelle s'applique chaque
                          mois aux élèves qui la suivent, celle d'inscription ne
                          joue qu'au moment de l'admission — un élève admis avant
                          d'entrer dans la famille garde ses frais d'origine. */}
                      <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                          Valeurs de référence de la famille
                        </div>
                        <div className="mt-1 flex flex-wrap gap-4 text-xs">
                          <span className="text-gray-600">
                            Mensuelle : <strong className="text-gray-900">{fmt(archive.reduction_mensuel)}</strong>
                            <span className="text-gray-400"> · appliquée chaque mois</span>
                          </span>
                          <span className="text-gray-600">
                            Inscription : <strong className="text-gray-900">{fmt(archive.reduction_inscription)}</strong>
                            <span className="text-gray-400"> · appliquée à l'admission seulement</span>
                          </span>
                        </div>
                      </div>

                      {/* Élèves */}
                      <div>
                        <div className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-2">
                          <Users size={14} className="text-[#246990]" />
                          Élèves au moment de l'archivage ({eleves.length})
                        </div>
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b-2 border-gray-200 bg-gray-100">
                              <th className="text-left py-2 px-3 font-semibold text-gray-600">Élève</th>
                              <th className="text-center py-2 px-3 font-semibold text-gray-600">Classe</th>
                              <th className="text-right py-2 px-3 font-semibold text-gray-600">Frais mensuel</th>
                              <th className="text-center py-2 px-3 font-semibold text-gray-600">Suit la valeur famille</th>
                              <th className="text-center py-2 px-3 font-semibold text-gray-600">Statut avant</th>
                            </tr>
                          </thead>
                          <tbody>
                            {eleves.map((eleve, idx) => (
                              <tr key={idx} className="border-b border-gray-100">
                                <td className="py-2 px-3 font-medium">{eleve.prenom} {eleve.nom}</td>
                                <td className="py-2 px-3 text-center">
                                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] font-medium">{eleve.classe_nom || '-'}</span>
                                </td>
                                <td className="py-2 px-3 text-right">{fmt(eleve.frais_mensuel)}</td>
                                <td className="py-2 px-3 text-center">
                                  {eleve.utilise_reduction_famille 
                                    ? <span className="text-green-600 font-semibold">Oui</span>
                                    : <span className="text-gray-400">Non</span>}
                                </td>
                                <td className="py-2 px-3 text-center">
                                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                                    eleve.statut_avant === 'actif' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                  }`}>{eleve.statut_avant || 'actif'}</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Info restauration */}
                      {archive.is_restored === 1 && (
                        <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                          <div className="text-xs font-semibold text-green-800 mb-1">Restauration effectuée</div>
                          <div className="text-xs text-green-700">
                            Restaurée le {fmtDate(archive.restored_at)}{archive.restored_by && ` par ${archive.restored_by}`}
                          </div>
                          {archive.restoration_note && <div className="text-xs text-green-600 mt-1 italic">{archive.restoration_note}</div>}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {!loading && filtered.length > ITEMS_PER_PAGE && (
        <div className="flex items-center justify-between bg-white rounded-lg border border-gray-200 shadow-sm p-4 mt-4">
          <div className="text-xs text-gray-500">
            Affichage {((currentPage - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, filtered.length)} sur {filtered.length} archive{filtered.length > 1 ? 's' : ''}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="px-2.5 py-1.5 text-xs font-medium rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              «
            </button>
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Précédent
            </button>
            
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(page => page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1)
              .map((page, idx, arr) => (
                <React.Fragment key={page}>
                  {idx > 0 && arr[idx - 1] !== page - 1 && (
                    <span className="px-1 text-gray-400 text-xs">...</span>
                  )}
                  <button
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                      currentPage === page
                        ? 'bg-amber-500 text-white shadow-sm'
                        : 'border border-gray-200 hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    {page}
                  </button>
                </React.Fragment>
              ))
            }
            
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Suivant
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="px-2.5 py-1.5 text-xs font-medium rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              »
            </button>
          </div>
        </div>
      )}

      {/* Dialog restauration */}
      {showConfirmRestaurer && archiveARestaurer && (
        <ConfirmDialog
          isOpen={showConfirmRestaurer}
          onClose={() => { setShowConfirmRestaurer(false); setArchiveARestaurer(null); }}
          onConfirm={handleRestaurer}
          title={`Restaurer la famille "${archiveARestaurer.famille_nom}" ?`}
          description={
            <div className="text-left space-y-3">
              <p className="font-medium text-gray-700">Cette action va :</p>
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-600 ml-2">
                <li>Réactiver la famille avec ses réductions ({fmt(archiveARestaurer.reduction_mensuel)}/mois)</li>
                <li>Réintégrer les {(archiveARestaurer.eleves_snapshot || []).length} élève(s) dans la famille</li>
                <li>Recalculer les paiements non réglés avec la réduction</li>
              </ul>
              
              <div className="p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700 flex items-start gap-2">
                <Info className="w-3 h-3 flex-shrink-0 mt-0.5" />
                <span>Les paiements déjà effectués ne seront pas modifiés. Les élèves déjà rattachés à une autre famille ne seront pas déplacés.</span>
              </div>

              {(archiveARestaurer.eleves_snapshot || []).length < 2 && (
                <div className="p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700 flex items-start gap-2">
                  <AlertCircle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                  <span>
                    <strong>Attention :</strong> Cette famille ne contient que {(archiveARestaurer.eleves_snapshot || []).length} élève(s). 
                    Vous disposez d'un <strong>délai de 7 jours</strong> pour ajouter un deuxième élève. Passé ce délai, la famille sera automatiquement re-archivée.
                  </span>
                </div>
              )}

              {archiveARestaurer.was_intersite === 1 && (
                <div className="p-2 bg-teal-50 border border-teal-200 rounded text-xs text-teal-700 flex items-start gap-2">
                  <Network className="w-3 h-3 flex-shrink-0 mt-0.5" />
                  <span>
                    Cette famille était inter-site. La restauration ne réactive <strong>pas</strong> le mode inter-site. 
                    Pour re-lier au réseau, utilisez "Actions → Activer Inter-Site" après restauration.
                  </span>
                </div>
              )}
            </div>
          }
          confirmText="Restaurer la famille"
          cancelText="Annuler"
          type="question"
          isLoading={restaurationLoading}
        />
      )}

      {/*
        ⚠️ L'ARBITRAGE, ET NON `<Toast>` EN DUR.
        Cet écran rendait le toast court directement : il ne pouvait donc PAS
        honorer une option `long`, le composant court n'ayant ni `options`, ni
        `title`, ni `long`. Et le seuil automatique des 130 caractères ne le
        sauvait pas — les messages de ce module mesurent entre 44 et 104
        caractères, donc tous condamnés aux 3 secondes du toast court.
        Même motif que FamillesNombreuses et EmployeesList, déjà en place.
      */}
      {/* Ici les appelants écrivent l'objet d'état directement : il leur suffit
          d'y ajouter `long: true` et `title` pour basculer. */}
      {toast.visible && (
        shouldUseLongToast(toast.message, toast.type, toast) ? (
          <LongToast
            show
            title={toast.title}
            message={toast.message}
            type={toast.type}
            onClose={() => setToast({ ...toast, visible: false })}
          />
        ) : (
          <Toast message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, visible: false })} />
        )
      )}
    </div>
  );
};

export default ArchiveFamilles;